import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const DEFAULT_BRAND = {
  slug: "interactmarketing",
  display_name: "Interact Marketing",
  tagline: "",
  logo_url: "",
  favicon_url: "",
  primary_color: "#1058A8",
  accent_color: "#0C4A8F",
  footer_html: "",
  contact_email: "",
  disabled_modules: [],
};

const SKIP_LABELS = ["www", "app", "api", "preview", "staging"];

function detectSlug() {
  const params = new URLSearchParams(window.location.search);
  const qs = params.get("slug") || params.get("brand");
  if (qs) return qs;
  const host = window.location.hostname;
  if (host === "localhost" || host.endsWith("emergentagent.com") || host.endsWith("emergent.host")) return "";
  const parts = host.split(".");
  if (parts.length > 2 && !SKIP_LABELS.includes(parts[0])) return parts[0];
  return "";
}

const BrandContext = createContext(DEFAULT_BRAND);

export function BrandProvider({ children }) {
  const slug = detectSlug();
  const cacheKey = `brand:${slug || "root"}`;
  const [brand, setBrand] = useState(() => {
    try {
      const cached = localStorage.getItem(cacheKey);
      return cached ? { ...DEFAULT_BRAND, ...JSON.parse(cached) } : DEFAULT_BRAND;
    } catch {
      return DEFAULT_BRAND;
    }
  });

  useEffect(() => {
    api
      .get("/branding", { params: slug ? { slug } : {} })
      .then((r) => {
        if (r.data?.slug) {
          setBrand({ ...DEFAULT_BRAND, ...r.data });
          try {
            localStorage.setItem(cacheKey, JSON.stringify(r.data));
          } catch {}
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--brand-primary", brand.primary_color || "#1058A8");
    document.documentElement.style.setProperty("--brand-accent", brand.accent_color || "#0C4A8F");
    document.title = `${brand.display_name} — Content Review Portal`;
    if (brand.favicon_url) {
      const link = document.querySelector("link[rel~='icon']");
      if (link) link.href = brand.favicon_url;
    }
  }, [brand]);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

export const useBrand = () => useContext(BrandContext);

export function brandFooterText(brand) {
  return (brand.footer_html || "").replace(/<[^>]+>/g, "").trim();
}

export function BrandLogo({ className = "h-9 w-auto", textClass = "text-xl", testId = "brand-logo" }) {
  const brand = useBrand();
  if (brand.logo_url) {
    return <img src={brand.logo_url} alt={brand.display_name} className={className} data-testid={testId} />;
  }
  if (brand.slug === "interactmarketing") {
    return <img src="/interact-logo-primary.png" alt="Interact Marketing" className={className} data-testid={testId} />;
  }
  return (
    <span
      data-testid={testId}
      className={`font-heading font-black tracking-tighter ${textClass}`}
      style={{ color: "var(--brand-primary)" }}
    >
      {brand.display_name}
    </span>
  );
}
